import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid2,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { downloadNashCert, fetchOnboarding, resendOnboarding, revokeOnboarding } from '../api/client.js';
import { TOTAL_STEPS, colors } from '@scriptpad/shared';

const STEP_LABELS: Record<number, string> = {
  1: 'Welcome',
  2: 'Practice mode',
  3: 'Verify practice',
  4: 'Upload NASH cert',
  5: 'Verify HPI-I',
  6: 'Identity',
  7: 'Complete',
};

export const OnboardingDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [snack, setSnack] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin-onboarding', id],
    queryFn: () => fetchOnboarding(id!),
    enabled: Boolean(id),
  });

  const resend = useMutation({
    mutationFn: () => resendOnboarding(id!),
    onSuccess: () => setSnack('Magic link resent'),
  });

  const revoke = useMutation({
    mutationFn: () => revokeOnboarding(id!),
    onSuccess: () => {
      setSnack('Onboarding revoked');
      void queryClient.invalidateQueries({ queryKey: ['admin-onboarding', id] });
    },
  });

  const downloadCert = useMutation({
    mutationFn: () => downloadNashCert(id!),
    onError: () => setSnack('Could not download the certificate'),
  });

  if (query.isLoading) return <Typography>Loading...</Typography>;
  if (query.isError || !query.data) return <Alert severity="error">Could not load onboarding.</Alert>;

  const o = query.data;

  return (
    <Stack spacing={3}>
      <Button onClick={() => navigate('/onboardings')} sx={{ alignSelf: 'flex-start' }}>
        Back to list
      </Button>

      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h5">
          {o.invitee.firstName} {o.invitee.lastName}
        </Typography>
        <Chip label={o.status.replace('_', ' ')} size="small" />
      </Stack>

      <Paper sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
          <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
            <strong>Magic link:</strong> {o.magicLinkUrl}
          </Typography>
          <IconButton
            size="small"
            onClick={() => {
              void navigator.clipboard.writeText(o.magicLinkUrl);
              setSnack('Link copied');
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <Button onClick={() => resend.mutate()} disabled={resend.isPending}>
            Resend email
          </Button>
          <Button color="warning" onClick={() => revoke.mutate()} disabled={revoke.isPending || o.status === 'expired'}>
            Revoke
          </Button>
        </Stack>
      </Paper>

      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Invitee
            </Typography>
            <Stack spacing={1}>
              <Row label="Email" value={o.invitee.email} />
              <Row label="Phone" value={o.invitee.contactNumber ?? '-'} />
              <Row label="Occupation" value={o.invitee.occupation ?? '-'} />
              <Row label="AHPRA #" value={o.invitee.ahpraNumber ?? '-'} />
              <Row label="Prescriber #" value={o.invitee.prescriberNumber ?? '-'} />
              <Row label="Practice" value={o.invitee.primaryPracticeName ?? '-'} />
              <Row label="Plan" value={o.invitee.plan ?? '-'} />
            </Stack>
          </Paper>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Progress
            </Typography>
            <Stack spacing={1}>
              <Row
                label="Current step"
                value={`${o.currentStep} / ${TOTAL_STEPS} - ${STEP_LABELS[o.currentStep] ?? ''}`}
              />
              <Row
                label="Practice mode"
                value={
                  o.practiceMode === 'organisation'
                    ? 'Healthcare organisation'
                    : o.practiceMode === 'sole_practitioner'
                      ? 'Sole practitioner'
                      : 'Not yet chosen'
                }
              />
              <Row label="Created" value={new Date(o.createdAt).toLocaleString('en-AU')} />
              <Row label="Last activity" value={new Date(o.lastActivityAt).toLocaleString('en-AU')} />
              <Row label="Expires" value={new Date(o.expiresAt).toLocaleString('en-AU')} />
              <Row
                label="Completed"
                value={o.completedAt ? new Date(o.completedAt).toLocaleString('en-AU') : '-'}
              />
              <Row
                label="Invited by"
                value={o.invitedBy ? `${o.invitedBy.name} (${o.invitedBy.email})` : '-'}
              />
            </Stack>
          </Paper>
        </Grid2>

        {o.nashCert && (
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                NASH PKI certificate
              </Typography>
              <Stack spacing={1}>
                <Row label="Subject CN" value={o.nashCert.subjectCN} />
                <Row label="Issuer CN" value={o.nashCert.issuerCN ?? '-'} />
                <Row label="HPI-O" value={o.nashCert.hpiO ?? '-'} />
                <Row label="Serial" value={o.nashCert.serialNumber ?? '-'} />
                <Row
                  label="Valid"
                  value={`${new Date(o.nashCert.validFrom).toLocaleDateString('en-AU')} - ${new Date(
                    o.nashCert.validTo,
                  ).toLocaleDateString('en-AU')}`}
                />
                <Row label="Verified" value={new Date(o.nashCert.verifiedAt).toLocaleString('en-AU')} />
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1.5} alignItems="flex-start">
                <Button
                  variant="outlined"
                  onClick={() => downloadCert.mutate()}
                  disabled={downloadCert.isPending}
                >
                  {downloadCert.isPending ? 'Preparing...' : 'Download certificate (.p12, no password)'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  The certificate file and PIC are stored encrypted at rest. The download contains the
                  private key with no password, so store it securely. Each download is recorded in the
                  audit log.
                </Typography>
              </Stack>
            </Paper>
          </Grid2>
        )}

        {o.governanceAgreement && (
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Sole-practitioner agreement
              </Typography>
              <Stack spacing={1}>
                <Row label="Version" value={o.governanceAgreement.agreementVersion} />
                <Row
                  label="Agreed at"
                  value={new Date(o.governanceAgreement.agreedAt).toLocaleString('en-AU')}
                />
                <Row label="IP" value={o.governanceAgreement.ipAddress ?? '-'} />
              </Stack>
            </Paper>
          </Grid2>
        )}

        {o.hpiIRecord && (
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                HPI-I
              </Typography>
              <Stack spacing={1}>
                <Row label="HPI-I" value={o.hpiIRecord.hpiI} />
                <Row
                  label="Recorded at"
                  value={new Date(o.hpiIRecord.recordedAt).toLocaleString('en-AU')}
                />
              </Stack>
            </Paper>
          </Grid2>
        )}

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Identity verification
            </Typography>
            <Stack spacing={1}>
              <Row label="Status" value={o.identity.status} />
              <Row label="Sumsub applicant ID" value={o.identity.sumsubApplicantId ?? '-'} />
              <Row label="Review answer" value={o.identity.reviewAnswer ?? '-'} />
              <Row
                label="Started"
                value={o.identity.startedAt ? new Date(o.identity.startedAt).toLocaleString('en-AU') : '-'}
              />
              <Row
                label="Completed"
                value={o.identity.completedAt ? new Date(o.identity.completedAt).toLocaleString('en-AU') : '-'}
              />
            </Stack>
          </Paper>
        </Grid2>
      </Grid2>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        message={snack}
      />
    </Stack>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', alignItems: 'baseline', gap: 1 }}>
    <Typography variant="body2" sx={{ color: colors.lightGrey }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);
